/**
 * Map the engine's per-module DISC-5 verdicts onto the app's result-summary
 * shape. Pure (no React Native / storage deps) so it is unit-testable on Node.
 *
 * The legacy result model is probability-centric (`predictions`,
 * `maxProbability`, `topConcernLabel`, severity thresholds). To keep every
 * existing screen working without a probability model, we derive a pseudo-score
 * per module from its verdict:
 *   - criteria met            → 1.0
 *   - determined, not met     → 0.0
 *   - undetermined (exhausted)→ 0.5
 * and expose the structured verdicts separately as `moduleVerdicts`.
 */
import {ModuleResult} from './session';

export type ModuleVerdict = {
  target: string;
  label: string;
  title: string;
  criteriaMet: boolean;
  determined: boolean;
  nQuestions: number;
};

export type VerdictSummary = {
  predictions: Record<string, number>;
  maxProbability: number;
  topConcernLabel: string;
  moduleVerdicts: ModuleVerdict[];
};

export const verdictScore = (r: {criteriaMet: boolean; determined: boolean}): number => {
  if (r.criteriaMet) {
    return 1;
  }
  return r.determined ? 0 : 0.5;
};

export const summariseVerdicts = (results: ModuleResult[]): VerdictSummary => {
  const moduleVerdicts: ModuleVerdict[] = results.map(r => ({
    target: r.target,
    label: r.label,
    title: r.title,
    criteriaMet: r.criteriaMet,
    determined: r.determined,
    nQuestions: r.nQuestions,
  }));

  const predictions: Record<string, number> = {};
  for (const r of results) {
    predictions[r.title] = verdictScore(r);
  }

  let topConcernLabel = 'wellbeing';
  let maxProbability = 0;
  for (const r of results) {
    const score = verdictScore(r);
    if (score > maxProbability || (score === maxProbability && topConcernLabel === 'wellbeing')) {
      maxProbability = score;
      topConcernLabel = r.title;
    }
  }

  return {predictions, maxProbability, topConcernLabel, moduleVerdicts};
};
